import { useEffect, useState } from 'react';
import { fetchUsers } from '../users/usersApi';
import type { EmergencyPlanPayload, EmergencyPlanStatus, EmergencyScenario } from './types';
import { PLAN_STATUS_LABEL, SCENARIO_LABEL } from './emergencyUtils';

const SCENARIOS = Object.keys(SCENARIO_LABEL) as EmergencyScenario[];
const STATUSES = Object.keys(PLAN_STATUS_LABEL) as EmergencyPlanStatus[];

interface PlanFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: EmergencyPlanPayload) => Promise<void>;
  initial?: Partial<EmergencyPlanPayload>;
  title: string;
}

function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function defaultPayload(): EmergencyPlanPayload {
  return {
    name: '',
    scenario: 'evacuacion',
    procedureNote: '',
    resources: '',
    nextDrillAt: null,
    status: 'activo',
  };
}

export function PlanFormModal({ open, onClose, onSubmit, initial, title }: PlanFormModalProps) {
  const [form, setForm] = useState<EmergencyPlanPayload>(defaultPayload());
  const [users, setUsers] = useState<Array<{ id: number; email: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = defaultPayload();
    setForm({
      ...base,
      ...initial,
      nextDrillAt: initial?.nextDrillAt ? toDateInput(initial.nextDrillAt) : null,
      responsibleUserId: initial?.responsibleUserId ?? null,
      status: initial?.status ?? base.status,
    });
    setError(null);

    void fetchUsers({ pageSize: 200 })
      .then((res) => setUsers(res.data.map((u) => ({ id: u.id, email: u.email }))))
      .catch(() => setUsers([]));
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        ...form,
        resources: form.resources?.trim() ? form.resources.trim() : null,
        nextDrillAt: form.nextDrillAt || null,
        responsibleUserId: form.responsibleUserId || null,
      });
      onClose();
    } catch {
      setError('No se pudo guardar el plan.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-card p-6 shadow-lg border border-border max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="plan-name">
              Nombre del plan
            </label>
            <input
              id="plan-name"
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
              maxLength={255}
              placeholder="Ej: Plan de evacuación — Planta Norte"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="plan-scenario">
                Escenario
              </label>
              <select
                id="plan-scenario"
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                value={form.scenario}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, scenario: e.target.value as EmergencyScenario }))
                }
              >
                {SCENARIOS.map((value) => (
                  <option key={value} value={value}>
                    {SCENARIO_LABEL[value]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="plan-status">
                Estado
              </label>
              <select
                id="plan-status"
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                value={form.status ?? 'activo'}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value as EmergencyPlanStatus }))
                }
              >
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {PLAN_STATUS_LABEL[value]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="plan-procedure">
              Procedimiento
            </label>
            <textarea
              id="plan-procedure"
              className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[96px]"
              value={form.procedureNote}
              onChange={(e) => setForm((prev) => ({ ...prev, procedureNote: e.target.value }))}
              required
              maxLength={10000}
              placeholder="Pasos a seguir, rutas de evacuación, responsables…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="plan-responsible">
              Responsable (opcional)
            </label>
            <select
              id="plan-responsible"
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              value={form.responsibleUserId ?? ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  responsibleUserId: e.target.value ? Number(e.target.value) : null,
                }))
              }
            >
              <option value="">Sin responsable asignado</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="plan-resources">
              Recursos (opcional)
            </label>
            <textarea
              id="plan-resources"
              className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[72px]"
              value={form.resources ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, resources: e.target.value }))}
              maxLength={10000}
              placeholder="Equipos, brigadas, contactos de emergencia..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="plan-next-drill">
              Próximo simulacro programado
            </label>
            <input
              id="plan-next-drill"
              type="date"
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              value={toDateInput(form.nextDrillAt ?? undefined)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  nextDrillAt: e.target.value || null,
                }))
              }
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
